import { useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { getPost, type BlogPost as BlogPostT } from '@/lib/blog';
import { useSeo } from '@/lib/seoHead';
import { Spinner } from '@/components/Spinner';

const SITE_URL = 'https://cnapractice.com';

export function BlogPost() {
  const { slug = '' } = useParams();
  const q = useQuery({
    queryKey: ['blog-post', slug],
    queryFn: () => getPost(slug),
    enabled: slug.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const post = q.data ?? null;

  // Render markdown → sanitised HTML once per post body change.
  const html = useMemo(() => {
    if (!post?.content) return '';
    const raw = marked.parse(post.content, { async: false }) as string;
    return DOMPurify.sanitize(raw);
  }, [post?.content]);

  return (
    <BlogShell>
      <BlogPostInner q={q} post={post} html={html} />
    </BlogShell>
  );
}

function BlogPostInner({
  q,
  post,
  html,
}: {
  q: ReturnType<typeof useQuery<BlogPostT | null>>;
  post: BlogPostT | null;
  html: string;
}) {
  // Hooks must be called unconditionally.
  const seoTitle = post?.seoTitle || post?.title || 'CNA Prep Pro Blog';
  const seoDesc = post?.seoDescription || post?.excerpt || 'CNA exam prep guides and tips.';
  const canonical = post ? `${SITE_URL}/blog/${post.slug}` : `${SITE_URL}/blog`;
  useSeo({
    title: `${seoTitle} · CNA Prep Pro`,
    description: seoDesc,
    canonical,
    ogImage: post?.coverImageUrl,
    ogType: 'article',
    jsonLd: post
      ? {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: post.title,
          description: seoDesc,
          image: post.coverImageUrl ? [post.coverImageUrl] : undefined,
          datePublished: post.publishedAt ? new Date(post.publishedAt).toISOString() : undefined,
          dateModified: post.updatedAt ? new Date(post.updatedAt).toISOString() : undefined,
          author: { '@type': 'Organization', name: post.author },
          publisher: { '@type': 'Organization', name: 'CNA Prep Pro', url: SITE_URL },
          mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
        }
      : undefined,
  });

  if (q.isLoading) {
    return <div className="flex justify-center py-16"><Spinner className="h-6 w-6 text-brand-600" /></div>;
  }
  if (!post || post.status !== 'published') {
    return <Navigate to="/blog" replace />;
  }
  const date = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : '';
  // Cheap reading-time estimate: 220 wpm on a stripped-down word count.
  const words = post.content.replace(/```[\s\S]*?```/g, ' ').replace(/[^\w\s]/g, ' ').split(/\s+/).filter(Boolean).length;
  const readingMins = Math.max(1, Math.round(words / 220));

  return (
    <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      <Link to="/blog" className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-700 mb-6">
        <ArrowLeft className="h-3.5 w-3.5" /> All posts
      </Link>

      <article>
        <header className="mb-8">
          {post.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {post.tags.slice(0, 3).map((t) => (
                <span
                  key={t}
                  className="px-2 py-0.5 rounded-full bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 text-[11px] font-semibold uppercase tracking-wide"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
          <h1 className="text-3xl sm:text-[2.5rem] font-bold tracking-tight leading-[1.15] mb-4">{post.title}</h1>
          {post.subtitle && (
            <p className="text-lg sm:text-xl text-stone-600 dark:text-stone-400 leading-relaxed mb-4">{post.subtitle}</p>
          )}
          <div className="flex items-center gap-2 text-[13px] text-stone-500">
            <span className="font-medium text-stone-700 dark:text-stone-300">{post.author}</span>
            {date && (
              <>
                <span>·</span>
                <time dateTime={post.publishedAt ? new Date(post.publishedAt).toISOString() : undefined}>{date}</time>
              </>
            )}
            <span>·</span>
            <span>{readingMins} min read</span>
          </div>
          {post.coverImageUrl && (
            <img
              src={post.coverImageUrl}
              alt=""
              loading="eager"
              className="mt-6 w-full max-h-[420px] object-cover rounded-2xl bg-stone-100 dark:bg-stone-800"
            />
          )}
        </header>

        <div
          className="prose prose-stone dark:prose-invert max-w-none prose-lg prose-headings:tracking-tight prose-h2:mt-12 prose-h2:mb-3 prose-h3:mt-10 prose-h3:mb-2 prose-p:leading-[1.75] prose-li:leading-[1.7] prose-a:text-brand-700 dark:prose-a:text-brand-400 prose-img:rounded-lg prose-blockquote:border-l-brand-500 prose-blockquote:text-stone-600 dark:prose-blockquote:text-stone-400"
          // HTML is DOMPurify-sanitised in the parent (admin-authored markdown).
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </article>

      <aside className="mt-14 rounded-2xl border border-brand-200 dark:border-brand-800 bg-brand-50/60 dark:bg-brand-900/20 p-5 sm:p-6 text-center">
        <h3 className="text-lg font-semibold mb-1">Try the practice exam</h3>
        <p className="text-sm text-stone-700 dark:text-stone-300 mb-3">
          25 free questions. No signup. ~15 minutes. Get a real readiness score.
        </p>
        <Link to="/trial/new" className="inline-flex items-center gap-1 rounded-lg bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 text-sm font-semibold">
          Start the free trial
          <ArrowRight className="h-4 w-4" />
        </Link>
      </aside>
    </main>
  );
}

function BlogShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950 text-stone-900 dark:text-stone-100">
      <header className="border-b border-stone-200 dark:border-stone-800 bg-stone-50/90 dark:bg-stone-950/90 backdrop-blur sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="text-lg font-semibold text-brand-700 dark:text-brand-400">CNA Prep Pro</Link>
          <nav className="flex items-center gap-3 sm:gap-5 text-sm">
            <Link to="/blog" className="text-stone-700 dark:text-stone-300 font-semibold">Blog</Link>
            <Link to="/" className="text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100">Home</Link>
            <Link to="/trial/new" className="inline-flex items-center gap-1 rounded-lg bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 font-semibold shadow-sm">
              Try free
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </nav>
        </div>
      </header>
      {children}
      <footer className="border-t border-stone-200 dark:border-stone-800 py-6 mt-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-xs text-stone-500 flex flex-col sm:flex-row items-center justify-between gap-2">
          <span>© {new Date().getFullYear()} CNA Prep Pro</span>
          <nav className="flex items-center gap-4">
            <Link to="/" className="hover:text-stone-700 dark:hover:text-stone-300">Home</Link>
            <Link to="/blog" className="hover:text-stone-700 dark:hover:text-stone-300">Blog</Link>
            <Link to="/trial/new" className="hover:text-stone-700 dark:hover:text-stone-300">Try free</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
